import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Bell, Copy, Eye, MoreHorizontal, Pencil, Power, RotateCcw, ScrollText, Trash2, UserPlus, Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmDialog, useConfirmDialog } from '@/components/shared/ConfirmDialog';
import { copyText } from '@/components/shared/CopyLink';
import type { OrganisationRow } from '@/api/endpoints/admin';
import {
  addOrganisationMember, deactivateOrganisation, deleteUserAccount, listOrganisationMembers, reactivateOrganisation,
  sendWelcomeNotification, softDeleteOrganisation, startImpersonation, updateOrganisation,
  type OrganisationMember,
} from '@/api/endpoints/organisationLifecycle';
import { useUpdateOrganisationUiFlags } from '@/hooks/mutations/useAdminMutations';
import { ApiErrorException } from '@/api/client';
import { useLifecycleError } from './lifecycleError';
import { homeForRole } from '@/lib/homeForRole';
import { emailFieldError } from '@/pages/auth/AuthCard';

/**
 * SU-1 (W7): everything a super admin does to one organisation, from its
 * row menu (AdminOrganisations). DES-13: edit is a form; deactivate is a
 * ConfirmDialog stating the impact; soft delete is a ConfirmDialog with the
 * typed name (checked by the server too, ENG-17); reactivate is reversible
 * (toast « Annuler »); restoring a soft-deleted one is a ConfirmDialog
 * without undo (REV-W7-F3). « Ouvrir en tant que » starts a read-only view of the
 * organisation (ENG-10) and lands on the member's home. SR2-7: the two UI
 * flag switches live here as checkbox items (they were row columns).
 */

export type OrgStatus = 'active' | 'disabled' | 'deleted';
export const statusOf = (org: OrganisationRow): OrgStatus =>
  (org.status ?? (org.is_active ? 'active' : 'disabled')) as OrgStatus;

/** The name the server asks to type: the company name, or the email when there is none. */
export const confirmNameOf = (org: Pick<OrganisationRow, 'company_name' | 'email'>) => org.company_name?.trim() || org.email;

const ORGS_KEY = ['admin', 'organisations'];

/** DES-16: the translated message first, then the server code in small monospace. */
export function ErrorLine({ error }: { error: unknown }) {
  const { message } = useLifecycleError();
  const code = error instanceof ApiErrorException ? error.code : undefined;
  return (
    <p role="alert" className="text-sm text-destructive">
      {message(error)}
      {code && <span className="ml-2 font-mono text-xs opacity-80">{code}</span>}
    </p>
  );
}

function useLifecycleMutations(org: OrganisationRow) {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const { notify: showErrorToast } = useLifecycleError();
  const refresh = () => qc.invalidateQueries({ queryKey: ORGS_KEY });
  const deactivate = useMutation({ mutationFn: () => deactivateOrganisation(org.id), onSettled: refresh });
  const reactivate = useMutation({ mutationFn: () => reactivateOrganisation(org.id), onSettled: refresh });
  const softDelete = useMutation({ mutationFn: (name: string) => softDeleteOrganisation(org.id, name), onSettled: refresh });
  const welcome = useMutation({ mutationFn: () => sendWelcomeNotification([org.email]) });

  // DES-13: reactivating a disabled organisation is reversible, so
  // « Annuler » (deactivate) instead of a confirmation. REV-W7-F3: restoring
  // a soft-deleted one goes through a ConfirmDialog instead (runRestore) and
  // offers no undo: deactivate would leave it disabled, not deleted.
  const runReactivate = () => reactivate.mutate(undefined, {
    onSuccess: () => toast.success(t('admin:organisations.lifecycle.reactivated', { company: org.company_name }), {
      action: { label: t('common:actions.undo'), onClick: () => deactivate.mutate(undefined, { onError: (err) => showErrorToast(err) }) },
    }),
    onError: (err) => showErrorToast(err),
  });
  const runWelcome = () => welcome.mutate(undefined, {
    onSuccess: ({ notified }) => (notified > 0
      ? toast.success(t('admin:organisations.lifecycle.welcomeSent', { company: org.company_name }))
      : toast.error(t('admin:organisations.lifecycle.welcomeNotSent'))),
    onError: (err) => showErrorToast(err),
  });
  return { deactivate, reactivate, softDelete, runReactivate, runWelcome };
}

/** « Ouvrir en tant que »: full reload so every store and cache starts as the member. */
export async function openAs(organisationId: string, userId?: string): Promise<void> {
  const started = await startImpersonation(organisationId, userId);
  window.location.assign(homeForRole({ isSuperAdmin: false, role: started.role }));
}

/**
 * The mutation lives in the row menu (always mounted), not in the menu
 * items: picking an item closes the menu and would drop the success toast.
 */
function useUiFlagToggle(org: OrganisationRow) {
  const { t } = useTranslation(['admin', 'common']);
  const { notify: showErrorToast } = useLifecycleError();
  const mutation = useUpdateOrganisationUiFlags();
  const flags = org.ui_flags;
  const set = (patch: Partial<OrganisationRow['ui_flags']>, label: string) => {
    // Undo restores only the flags this action changed.
    const before = Object.fromEntries(Object.keys(patch).map((k) => [k, flags[k as keyof typeof flags]])) as Partial<OrganisationRow['ui_flags']>;
    mutation.mutate({ id: org.id, patch }, {
      onSuccess: (after) => {
        const on = patch.ui_refonte !== undefined ? after.ui_refonte : after.call_page;
        toast.success(t(on ? 'admin:organisations.uiFlags.enabled' : 'admin:organisations.uiFlags.disabled', { flag: label, company: org.company_name }), {
          action: { label: t('common:actions.undo'), onClick: () => mutation.mutate({ id: org.id, patch: before }, { onError: (err) => showErrorToast(err) }) },
        });
      },
      onError: (err) => showErrorToast(err),
    });
  };
  return { set, pending: mutation.isPending };
}

function UiFlagItems({ org, toggle }: { org: OrganisationRow; toggle: ReturnType<typeof useUiFlagToggle> }) {
  const { t } = useTranslation(['admin', 'common']);
  const flags = org.ui_flags;
  const { set } = toggle;
  const shell = t('admin:organisations.uiFlags.shell');
  const callPage = t('admin:organisations.uiFlags.callPage');
  return (
    <>
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t('admin:organisations.columns.uiFlags')}</DropdownMenuLabel>
      <DropdownMenuCheckboxItem
        checked={flags.ui_refonte}
        disabled={toggle.pending}
        onCheckedChange={(v: boolean) => set(v ? { ui_refonte: true } : { ui_refonte: false, call_page: false }, shell)}
        aria-label={`${shell} · ${org.company_name}`}
      >
        {shell}
      </DropdownMenuCheckboxItem>
      <DropdownMenuCheckboxItem
        checked={flags.ui_refonte && flags.call_page}
        disabled={toggle.pending || !flags.ui_refonte}
        onCheckedChange={(v: boolean) => set({ call_page: v }, callPage)}
        aria-label={`${callPage} · ${org.company_name}`}
      >
        <span className="flex flex-col">
          <span>{callPage}</span>
          {!flags.ui_refonte && <span className="text-xs text-muted-foreground">{t('admin:organisations.uiFlags.callPageNeedsShell')}</span>}
        </span>
      </DropdownMenuCheckboxItem>
    </>
  );
}

type Open = null | 'edit' | 'deactivate' | 'delete' | 'restore' | 'members';

export function OrganisationRowMenu({ org, onOpenTeam, onAuditLog }: { org: OrganisationRow; onOpenTeam: () => void; onAuditLog: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const [open, setOpen] = useState<Open>(null);
  const { notify: showErrorToast } = useLifecycleError();
  const status = statusOf(org);
  const m = useLifecycleMutations(org);
  const uiFlags = useUiFlagToggle(org);
  const members = org.usage?.members;

  const runOpenAs = () => {
    openAs(org.id).catch((err) => showErrorToast(err));
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-11 w-11" aria-label={t('admin:organisations.actions.menu', { company: org.company_name })}>
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem onSelect={onOpenTeam} className="gap-2" disabled={status !== 'active'}>
            <Users className="size-4" /> {t('admin:organisations.actions.openTeam')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={runOpenAs} className="gap-2" disabled={status !== 'active'}>
            <Eye className="size-4" /> {t('admin:organisations.lifecycle.openAs')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setOpen('members')} className="gap-2">
            <UserPlus className="size-4" /> {t('admin:organisations.lifecycle.members')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setOpen('edit')} className="gap-2">
            <Pencil className="size-4" /> {t('admin:organisations.lifecycle.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={m.runWelcome} className="gap-2" disabled={status !== 'active'}>
            <Bell className="size-4" /> {t('admin:organisations.lifecycle.sendWelcome')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <UiFlagItems org={org} toggle={uiFlags} />
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onAuditLog} className="gap-2">
            <ScrollText className="size-4" /> {t('admin:organisations.actions.auditLog')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => { void copyText(org.id).then(() => toast.success(t('admin:organisations.actions.idCopied'))); }}
            className="gap-2"
          >
            <Copy className="size-4" /> {t('admin:organisations.actions.copyId')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {status === 'active' ? (
            <DropdownMenuItem onSelect={() => setOpen('deactivate')} className="gap-2">
              <Power className="size-4" /> {t('admin:organisations.lifecycle.deactivate')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={status === 'deleted' ? () => setOpen('restore') : m.runReactivate} className="gap-2">
              <RotateCcw className="size-4" /> {t(status === 'deleted' ? 'admin:organisations.lifecycle.restore' : 'admin:organisations.lifecycle.reactivate')}
            </DropdownMenuItem>
          )}
          {status !== 'deleted' && (
            <DropdownMenuItem onSelect={() => setOpen('delete')} className="gap-2 text-destructive focus:text-destructive">
              <Trash2 className="size-4" /> {t('admin:organisations.lifecycle.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {open === 'edit' && <EditOrganisationDialog org={org} onClose={() => setOpen(null)} />}
      {open === 'members' && <OrganisationMembersDialog org={org} onClose={() => setOpen(null)} />}

      <ConfirmDialog
        open={open === 'deactivate'}
        onOpenChange={(o) => setOpen(o ? 'deactivate' : null)}
        title={t('admin:organisations.lifecycle.deactivateTitle', { company: org.company_name })}
        description={
          <span className="block space-y-2">
            <span className="block">{typeof members === 'number'
              ? t('admin:organisations.lifecycle.deactivateImpact', { count: members })
              : t('admin:organisations.lifecycle.deactivateImpactUnknown')}</span>
            <span className="block">{t('admin:organisations.lifecycle.deactivateKept')}</span>
          </span>
        }
        confirmLabel={t('admin:organisations.lifecycle.deactivate')}
        onConfirm={async () => {
          try {
            await m.deactivate.mutateAsync();
          } catch (err) {
            showErrorToast(err);
            throw err;
          }
          setOpen(null);
          toast.success(t('admin:organisations.lifecycle.deactivated', { company: org.company_name }));
        }}
      />

      <ConfirmDialog
        open={open === 'restore'}
        onOpenChange={(o) => setOpen(o ? 'restore' : null)}
        title={t('admin:organisations.lifecycle.restoreTitle', { company: org.company_name })}
        description={t('admin:organisations.lifecycle.restoreImpact')}
        confirmLabel={t('admin:organisations.lifecycle.restore')}
        // QA-W7-10: restoring is not destructive (primary button, not red).
        tone="default"
        onConfirm={async () => {
          try {
            await m.reactivate.mutateAsync();
          } catch (err) {
            showErrorToast(err);
            throw err;
          }
          setOpen(null);
          toast.success(t('admin:organisations.lifecycle.reactivated', { company: org.company_name }));
        }}
      />

      <ConfirmDialog
        open={open === 'delete'}
        onOpenChange={(o) => setOpen(o ? 'delete' : null)}
        title={t('admin:organisations.lifecycle.deleteTitle', { company: org.company_name })}
        description={t('admin:organisations.lifecycle.deleteImpact')}
        confirmLabel={t('admin:organisations.lifecycle.delete')}
        typedName={confirmNameOf(org)}
        onConfirm={async () => {
          try {
            await m.softDelete.mutateAsync(confirmNameOf(org));
          } catch (err) {
            showErrorToast(err);
            throw err;
          }
          setOpen(null);
          toast.success(t('admin:organisations.lifecycle.deleted', { company: org.company_name }));
        }}
      />
    </>
  );
}

function EditOrganisationDialog({ org, onClose }: { org: OrganisationRow; onClose: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const [company, setCompany] = useState(org.company_name);
  const [contact, setContact] = useState(org.contact_name);
  const [touched, setTouched] = useState(false);
  const save = useMutation({
    mutationFn: () => updateOrganisation(org.id, {
      ...(company.trim() !== org.company_name ? { company_name: company.trim() } : {}),
      ...(contact.trim() !== org.contact_name ? { contact_name: contact.trim() } : {}),
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ORGS_KEY });
      toast.success(t('admin:organisations.lifecycle.saved', { company: company.trim() }));
      onClose();
    },
  });
  const companyError = touched && !company.trim();
  const contactError = touched && !contact.trim();
  const unchanged = company.trim() === org.company_name && contact.trim() === org.contact_name;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!company.trim() || !contact.trim() || unchanged) return;
    save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('admin:organisations.lifecycle.editTitle')}</DialogTitle>
          <DialogDescription>{t('admin:organisations.lifecycle.editHint', { email: org.email })}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-org-company">{t('admin:organisations.createDialog.companyName')}</Label>
            <Input id="edit-org-company" value={company} onChange={(e) => setCompany(e.target.value)} aria-invalid={companyError || undefined} />
            {companyError && <p className="text-xs text-destructive">{t('admin:organisations.createDialog.required')}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-org-contact">{t('admin:organisations.createDialog.contactName')}</Label>
            <Input id="edit-org-contact" value={contact} onChange={(e) => setContact(e.target.value)} aria-invalid={contactError || undefined} />
            {contactError && <p className="text-xs text-destructive">{t('admin:organisations.createDialog.required')}</p>}
          </div>
          {save.error && <ErrorLine error={save.error} />}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t('common:actions.cancel')}</Button>
            <Button type="submit" disabled={save.isPending || unchanged}>
              {save.isPending ? t('admin:organisations.lifecycle.saving') : t('admin:organisations.lifecycle.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const ROLE_LABEL: Record<OrganisationMember['role'], string> = { tenant_admin: 'admin', caller: 'caller', viewer: 'viewer' };

/**
 * The organisation's members, with « Ouvrir en tant que » per member,
 * « Supprimer le compte » (DELETE /admin/users/:id, typed email) and
 * « Ajouter un membre » by invitation (POST /admin/organisations/:id/users).
 */
export function OrganisationMembersDialog({ org, onClose }: { org: OrganisationRow; onClose: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const key = ['admin', 'organisations', org.id, 'members'];
  const query = useQuery({ queryKey: key, queryFn: ({ signal }) => listOrganisationMembers(org.id, { signal }) });
  const [ask, confirmDialog] = useConfirmDialog();
  const { notify: showErrorToast } = useLifecycleError();
  const remove = useMutation({
    mutationFn: (userId: string) => deleteUserAccount(userId),
    onSettled: () => { void qc.invalidateQueries({ queryKey: key }); void qc.invalidateQueries({ queryKey: ORGS_KEY }); },
  });
  const active = statusOf(org) === 'active';

  const onDelete = async (member: OrganisationMember) => {
    const ok = await ask({
      title: t('admin:organisations.lifecycle.deleteAccountTitle', { email: member.email }),
      description: t('admin:organisations.lifecycle.deleteAccountImpact'),
      confirmLabel: t('admin:organisations.lifecycle.deleteAccount'),
      typedName: member.email,
    });
    if (!ok) return;
    remove.mutate(member.user_id, {
      onSuccess: () => toast.success(t('admin:organisations.lifecycle.accountDeleted', { email: member.email })),
      onError: (err) => (err instanceof ApiErrorException && err.code === 'LAST_ADMIN_OF_ORGANISATION'
        ? toast.error(t('admin:organisations.lifecycle.lastAdmin'))
        : showErrorToast(err)),
    });
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('admin:organisations.lifecycle.membersTitle', { company: org.company_name })}</DialogTitle>
          <DialogDescription>{t('admin:organisations.lifecycle.membersHint')}</DialogDescription>
        </DialogHeader>
        {query.isLoading && <p className="text-sm text-muted-foreground" aria-busy>{t('admin:organisations.lifecycle.loading')}</p>}
        {query.error && (
          <div className="space-y-2">
            <ErrorLine error={query.error} />
            <Button size="sm" variant="outline" onClick={() => void query.refetch()}>{t('common:actions.retry')}</Button>
          </div>
        )}
        {query.data && query.data.length === 0 && <p className="text-sm text-muted-foreground">{t('admin:organisations.lifecycle.noMembers')}</p>}
        {query.data && query.data.length > 0 && (
          <ul className="divide-y rounded-md border">
            {query.data.map((member) => (
              <li key={member.membership_id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{member.full_name || member.email}</p>
                  <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                </div>
                <Badge variant="secondary">{t(`admin:organisationMembers.roles.${ROLE_LABEL[member.role]}`)}</Badge>
                {!member.is_active && <Badge variant="outline">{t('common:states.inactive')}</Badge>}
                <Button
                  size="sm" variant="ghost" className="min-h-11 gap-1"
                  disabled={!active || !member.is_active}
                  onClick={() => { openAs(org.id, member.user_id).catch((err) => showErrorToast(err)); }}
                  aria-label={t('admin:organisations.lifecycle.openAsMember', { email: member.email })}
                >
                  <Eye className="size-4" aria-hidden /> {t('admin:organisations.lifecycle.openAs')}
                </Button>
                <Button
                  size="icon" variant="ghost" className="h-11 w-11 text-destructive"
                  disabled={remove.isPending}
                  onClick={() => void onDelete(member)}
                  aria-label={t('admin:organisations.lifecycle.deleteAccountOf', { email: member.email })}
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        {/* REV-W7-F12: the server refuses to add a member to a disabled or
            deleted organisation; say so instead of offering the form. */}
        {active
          ? <AddMemberForm organisationId={org.id} onAdded={() => void qc.invalidateQueries({ queryKey: key })} />
          : <p className="border-t pt-4 text-sm text-muted-foreground">{t('admin:organisations.lifecycle.addMemberInactive')}</p>}
        {confirmDialog}
      </DialogContent>
    </Dialog>
  );
}

function AddMemberForm({ organisationId, onAdded }: { organisationId: string; onAdded: () => void }) {
  const { t } = useTranslation(['admin', 'common']);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'tenant_admin' | 'caller' | 'viewer'>('caller');
  const [emailError, setEmailError] = useState<string | undefined>();
  const add = useMutation({ mutationFn: () => addOrganisationMember(organisationId, { email: email.trim().toLowerCase(), fullName: fullName.trim() || undefined, role }) });
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const err = emailFieldError(email);
    setEmailError(err);
    if (err) return;
    add.mutate(undefined, {
      onSuccess: (res) => {
        if (res.invited && !res.email_sent) toast.warning(t('admin:organisations.lifecycle.memberInvitedNotSent', { email }));
        else toast.success(t('admin:organisations.lifecycle.memberInvited', { email }));
        setEmail(''); setFullName('');
        onAdded();
      },
    });
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-3 border-t pt-4">
      <p className="text-sm font-medium">{t('admin:organisations.lifecycle.addMember')}</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="add-member-email">{t('admin:organisations.lifecycle.memberEmail')}</Label>
          <Input id="add-member-email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setEmailError(undefined); }} aria-invalid={emailError ? true : undefined} />
          {emailError && <p className="text-xs text-destructive">{t(emailError)}</p>}
        </div>
        <div className="space-y-1">
          <Label htmlFor="add-member-name">{t('admin:organisations.createDialog.adminFullName')}</Label>
          <Input id="add-member-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </div>
        <div className="space-y-1">
          <span className="block text-sm font-medium leading-none" aria-hidden>{t('admin:organisations.lifecycle.memberRole')}</span>
          <Select value={role} onValueChange={(v) => setRole(v as typeof role)}>
            <SelectTrigger aria-label={t('admin:organisations.lifecycle.memberRole')}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tenant_admin">{t('admin:organisationMembers.roles.admin')}</SelectItem>
              <SelectItem value="caller">{t('admin:organisationMembers.roles.caller')}</SelectItem>
              <SelectItem value="viewer">{t('admin:organisationMembers.roles.viewer')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {add.error && <ErrorLine error={add.error} />}
      <p className="text-xs text-muted-foreground">{t('admin:organisations.lifecycle.memberInviteHint')}</p>
      <div className="flex justify-end">
        <Button type="submit" disabled={add.isPending}>{add.isPending ? t('admin:organisations.lifecycle.inviting') : t('admin:organisations.lifecycle.invite')}</Button>
      </div>
    </form>
  );
}
